import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  constants,
  SiteConfigManager,
  useApplicationState,
  useDetection,
  usePaymentMethods,
} from '@atlas/core';
import { Button, Icon, Label, Loader, MoneyInput } from '@atlas/ui';

import { useBonusesContext } from 'src/components/BonusesProvider';
import QuickDepositPaymentMethod from 'src/components/QuickDepositPopup/QuickDepositPaymentMethod';
import useFormatter from 'src/hooks/useFormatter';

import { useDepositSubmit } from './hooks/useDepositSubmit';
import { useInitialPayment } from './hooks/useInitialPayment';
import QuickDepositBonuses from './QuickDepositBonuses';
import QuickDepositPaymentMethodDropdown from './QuickDepositPaymentMethodDropdown';

export const BUTTON_POSITION = {
  BOTTOM: 'bottom',
  INLINE: 'inline',
};

const QuickDepositFormContent = ({
  buttonPosition = BUTTON_POSITION.INLINE,
  closeQRCodeOnSuccess,
  onClose,
  onDepositStart,
  onDepositSuccess,
  setRedirectUrl,
  titleType,
}) => {
  const {
    wallet: { currency },
  } = useApplicationState();

  const history = useHistory();
  const { currencyFormatter } = useFormatter();

  const [isInputActive, setIsInputActive] = useState(false);
  const [value, setValue] = useState('');
  const { setDepositAmount, setPaymentMethod: setBonusesPaymentMethod } = useBonusesContext();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { paymentMethods } = usePaymentMethods(
    constants.PAYMENT_OPERATION.DEPOSIT,
    constants.DEVICE_TYPES.DESKTOP,
  );
  const { initialValue, paymentMethod, setPaymentMethod } = useInitialPayment(paymentMethods);

  const showInPopup = SiteConfigManager.getConfig('config.config.registration.showInPopup', false);
  const { browser, ip, locale, os } = useDetection();

  useEffect(() => {
    if (initialValue && value === '') {
      setValue(initialValue);
    }
  }, [initialValue, value]);

  const { error, handleDeposit, loading, setError } = useDepositSubmit({
    browser,
    closeQRCodeOnSuccess,
    history,
    ip,
    locale,
    onClose,
    onDepositStart,
    onDepositSuccess,
    os,
    paymentMethod,
    setRedirectUrl,
    showInPopup,
    value,
  });

  useEffect(() => {
    if (!paymentMethod || !value) return;
    const numValue = Number(value);
    if (!numValue || Number.isNaN(numValue)) return;

    if (paymentMethod.minAmount && numValue < paymentMethod.minAmount) {
      setValue(paymentMethod.minAmount);
      setError(null);
    } else if (paymentMethod.maxAmount && numValue > paymentMethod.maxAmount) {
      setValue(paymentMethod.maxAmount);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethod]);

  useEffect(() => {
    if (paymentMethod) {
      setBonusesPaymentMethod(paymentMethod);
    }
  }, [paymentMethod, setBonusesPaymentMethod]);

  const handleValueChange = newValue => {
    setDepositAmount(newValue);
    setValue(newValue);
    if (error) setError(null);
  };

  const handleBlur = useCallback(() => {
    setIsInputActive(false);
    if (!paymentMethod || !value) return;
    const numValue = Number(value);
    if (!numValue || Number.isNaN(numValue)) return;

    if (paymentMethod.maxAmount && numValue > paymentMethod.maxAmount) {
      setValue(paymentMethod.maxAmount);
    } else if (paymentMethod.minAmount && numValue < paymentMethod.minAmount) {
      setValue(paymentMethod.minAmount);
    }
  }, [paymentMethod, value]);

  const disabledDepositButton = !value || loading;

  return (
    <>
      <div className="quick-deposit-content">
        {paymentMethod ? (
          <>
            <div className="quick-deposit-form">
              {isDropdownOpen && paymentMethods.length > 1 && (
                <QuickDepositPaymentMethodDropdown
                  onSelect={pm => {
                    setPaymentMethod(pm);
                    setBonusesPaymentMethod(pm);
                    setIsDropdownOpen(false);
                  }}
                  paymentMethod={paymentMethod}
                  paymentMethods={paymentMethods}
                />
              )}
              <div className="quick-deposit-input">
                <QuickDepositPaymentMethod
                  isDropdownOpen={isDropdownOpen}
                  paymentMethod={paymentMethod}
                  setIsDropdownOpen={setIsDropdownOpen}
                  withDropdown={paymentMethods.length > 1}
                />
                <MoneyInput
                  active={isInputActive}
                  currency={currency}
                  error={error}
                  label="amount"
                  onBlur={handleBlur}
                  onChange={handleValueChange}
                  onFocus={() => setIsInputActive(true)}
                  value={value}
                />
                {buttonPosition === BUTTON_POSITION.INLINE && (
                  <Button
                    className="deposit-button"
                    disabled={disabledDepositButton}
                    label="deposit"
                    loading={loading}
                    onClick={handleDeposit}
                    size="big"
                  />
                )}
              </div>
              {error && (
                <div className="quick-deposit-form-error">
                  <Icon className="error-icon" height={18} name="error" width={18} />
                  <Label className="error-message" message={error.message} />
                </div>
              )}
            </div>

            <QuickDepositBonuses currencyFormatter={currencyFormatter} titleType={titleType} />

            {buttonPosition === BUTTON_POSITION.BOTTOM && (
              <Button
                className="deposit-button"
                disabled={disabledDepositButton}
                label="deposit"
                loading={loading}
                onClick={handleDeposit}
                size="big"
              />
            )}
          </>
        ) : (
          <Loader />
        )}
      </div>
    </>
  );
};

QuickDepositFormContent.propTypes = {
  buttonPosition: PropTypes.oneOf(['inline', 'bottom']),
  closeQRCodeOnSuccess: PropTypes.bool,
  onClose: PropTypes.func,
  onDepositStart: PropTypes.func,
  onDepositSuccess: PropTypes.func,
  setRedirectUrl: PropTypes.func,
  titleType: PropTypes.string,
};

const QuickDepositForm = props => {
  return <QuickDepositFormContent {...props} />;
};

QuickDepositForm.propTypes = QuickDepositFormContent.propTypes;

export default QuickDepositForm;
